import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, AlertTriangle, Package, X } from 'lucide-react';
import { fetchInventory, updateInventory } from '../services/api';

const Inventory = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [restockModal, setRestockModal] = useState({ show: false, product: null, quantity: '' });

  useEffect(() => {
    loadInventory();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, showLowStock]);

  const loadInventory = async () => {
    try {
      const data = await fetchInventory();
      setProducts(data);
    } catch (error) {
      console.error('Failed to load inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (showLowStock) {
      filtered = filtered.filter(product => product.quantity <= product.reorder_level);
    }

    setFilteredProducts(filtered);
  };

  const getStockStatus = (product) => {
    if (product.quantity === 0) {
      return { label: 'Out of Stock', color: 'text-red-600' };
    } else if (product.quantity <= product.reorder_level) {
      return { label: 'Low Stock', color: 'text-yellow-600' };
    }
    return { label: 'In Stock', color: 'text-green-600' };
  };

  const openRestockModal = (product) => {
    setRestockModal({ show: true, product, quantity: '' });
  };

  const closeRestockModal = () => {
    setRestockModal({ show: false, product: null, quantity: '' });
  };

  const handleRestock = async () => {
    const addQuantity = parseInt(restockModal.quantity);
    if (!addQuantity || addQuantity <= 0) {
      alert('Please enter a valid quantity');
      return;
    }

    try {
      const newQuantity = restockModal.product.quantity + addQuantity;
      const response = await updateInventory(restockModal.product.id, { quantity: newQuantity });

      if (!response) {
        throw new Error('No response from server');
      }

      // Reload inventory from server to ensure data is fresh
      await loadInventory();

      alert(`Added ${addQuantity} units to ${restockModal.product.name}`);
      closeRestockModal();
    } catch (error) {
      console.error('Failed to restock:', error);
      alert(`Failed to restock product: ${error.message || 'Please try again.'}`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading inventory...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Inventory</h1>
        <Link to="/inventory/new" className="btn-primary flex items-center gap-2">
          <Plus size={20} />
          Add Product
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search products by name or SKU..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showLowStock}
              onChange={(e) => setShowLowStock(e.target.checked)}
              className="w-4 h-4"
            />
            <span>Show Low Stock Only</span>
          </label>
        </div>
      </div>

      {/* Inventory Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No products found. Click "Add Product" to add your first product.
          </div>
        ) : (
          filteredProducts.map((product) => {
            const stockStatus = getStockStatus(product);
            return (
              <div key={product.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-primary-100 p-3 rounded-lg">
                    <Package className="text-primary-600" size={24} />
                  </div>
                  {product.quantity <= product.reorder_level && (
                    <AlertTriangle className="text-yellow-500" size={20} />
                  )}
                </div>

                <h3 className="font-semibold text-lg mb-2">{product.name}</h3>
                <p className="text-sm text-gray-500 mb-4">SKU: {product.sku}</p>

                <div className="space-y-2 border-t pt-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Quantity:</span>
                    <span className="font-medium">{product.quantity} units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Price:</span>
                    <span className="font-medium">${Number(product.price || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`font-medium ${stockStatus.color}`}>
                      {stockStatus.label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reorder Level:</span>
                    <span className="text-sm">{product.reorder_level}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex gap-2">
                  <button
                    onClick={() => navigate(`/inventory/${product.id}/edit`)}
                    className="flex-1 btn-secondary text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => openRestockModal(product)}
                    className="flex-1 btn-primary text-sm"
                  >
                    Restock
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Restock Modal */}
      {restockModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Restock Product</h3>
              <button onClick={closeRestockModal} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Product</p>
                <p className="font-medium">{restockModal.product?.name}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Current Stock</p>
                <p className="font-medium">{restockModal.product?.quantity} units</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add Quantity *
                </label>
                <input
                  type="number"
                  min="1"
                  value={restockModal.quantity}
                  onChange={(e) => setRestockModal({ ...restockModal, quantity: e.target.value })}
                  className="input-field"
                  placeholder="Enter quantity to add"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleRestock()}
                />
              </div>

              {restockModal.quantity && (
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">New Stock Level</p>
                  <p className="font-semibold text-lg text-green-600">
                    {restockModal.product?.quantity + parseInt(restockModal.quantity || 0)} units
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleRestock}
                  className="flex-1 btn-primary"
                >
                  Confirm Restock
                </button>
                <button
                  onClick={closeRestockModal}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
